import config from 'config';

import type { QuickReply, QuickReplyFileUpload } from '../types/quickReply';
import type {
  GoogleSignInBody,
  MfaResendBody,
  MfaVerifyBody,
  MicrosoftSignInBody,
  SignInBody,
  SignInResponse,
  User,
  UserAttributes,
} from '../types/user';
import baseApi from './baseApi';

const userApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    signInUser: build.mutation({
      query: (body: SignInBody) => ({
        // Same-origin (config.authUrl) so the refresh cookie this sets is first-party; an
        // absolute URL makes RTK bypass baseApi's cross-origin baseUrl. See server.js proxy.
        url: `${config.authUrl}/auth`,
        method: 'POST',
        body,
        // Auth endpoints exchange the httpOnly refresh-token cookie; everything else
        // stays cookie-less (the API's public-widget CORS paths don't allow credentials).
        credentials: 'include',
      }),
      transformResponse: (response: SignInResponse) => response,
    }),
    verifyMfaCode: build.mutation({
      query: (body: MfaVerifyBody) => ({
        // Same-origin: this completes login and sets the first-party refresh cookie.
        url: `${config.authUrl}/auth/mfa/verify`,
        method: 'POST',
        body,
        credentials: 'include',
      }),
      transformResponse: (response: User) => response,
    }),
    signInWithGoogle: build.mutation({
      query: (body: GoogleSignInBody) => ({
        // Same-origin: completes login directly (no MFA step) and sets the first-party
        // refresh cookie. See POST /auth/google in saas_back for the trust rationale.
        url: `${config.authUrl}/auth/google`,
        method: 'POST',
        body,
        credentials: 'include',
        // Anti-CSRF: the backend requires this header on session-establishing endpoints so a
        // cross-site <form> can't post a token and plant a session (login CSRF).
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }),
      transformResponse: (response: User) => response,
    }),
    signInWithMicrosoft: build.mutation({
      query: (body: MicrosoftSignInBody) => ({
        // Same-origin: completes login directly (no MFA step) and sets the first-party
        // refresh cookie. See POST /auth/microsoft in saas_back for the trust rationale.
        url: `${config.authUrl}/auth/microsoft`,
        method: 'POST',
        body,
        credentials: 'include',
        // Anti-CSRF: required by the backend on session-establishing endpoints (see google).
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }),
      transformResponse: (response: User) => response,
    }),
    logoutUser: build.mutation<{ ok: boolean }, void>({
      query: () => ({
        // Same-origin: clears the first-party refresh cookie on this origin.
        url: `${config.authUrl}/auth/logout`,
        method: 'POST',
        credentials: 'include',
        // CSRF gate: backend rejects cookie-only POSTs without this custom header.
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }),
    }),
    resendMfaCode: build.mutation({
      query: (body: MfaResendBody) => ({
        url: '/auth/mfa/resend',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { ok: boolean }) => response,
    }),
    getUser: build.query({
      query: () => ({
        url: '/user',
        method: 'GET',
      }),
      transformResponse: (response: User): User => response,
      providesTags: ['CurrentUser'],
    }),
    getQuickReplies: build.query<QuickReply[], void>({
      query: () => ({
        url: '/user/quick-replies',
        method: 'GET',
      }),
      providesTags: ['QuickReplies'],
    }),
    updatePassword: build.mutation({
      query: (body: { currentPassword: string; newPassword: string }) => ({
        url: '/user/update-password',
        method: 'PUT',
        body,
      }),
    }),
    getAssignableUsers: build.mutation({
      query: (clientId: number) => ({
        url: `/user/assignable-users/${clientId}`,
        method: 'GET',
      }),
      transformResponse: (
        response: { assignableUsers?: User[]; error?: string } | null
      ): { assignableUsers?: User[]; error?: string } | null => response,
    }),
    assignClientToUser: build.mutation({
      query: (body: { userId: number; clientToAssignId: number }) => ({
        url: '/user/assign-client',
        method: 'PATCH',
        body,
      }),
      // Assigning moves a client from pending → assigned; refresh the queue list and its tab badges,
      // which share the Clients/LIST cache entry.
      invalidatesTags: [{ type: 'Clients' as const, id: 'LIST' }],
    }),
    getUsers: build.query({
      query: (args: { businessUnitId?: number } = {}) => ({
        url: '/user/users',
        method: 'GET',
        params: args.businessUnitId != null ? { businessUnitId: args.businessUnitId } : undefined,
      }),
      transformResponse: (response: {
        users: (User & { businessUnitNames: string[] })[];
      }): (User & { businessUnitNames: string[] })[] => response.users,
    }),
    handleFirebaseNotificationTokens: build.mutation({
      query: (body: { newNotificationToken?: string | null }) => ({
        url: '/user/handle-firebase-notification-tokens',
        method: 'POST',
        body,
      }),
    }),
    updateCurrentUser: build.mutation({
      // Self-service update of the current user's own editable attributes (name, quick replies…).
      query: (body: { name?: string; quickReplies?: QuickReply[] }) => ({
        url: '/user',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, body) => [body.name ? 'CurrentUser' : 'QuickReplies'],
    }),
    passwordRecovery: build.mutation({
      query: (props: { email?: string; rut?: string; recaptchaToken?: string }) => ({
        url: '/auth/password/recovery',
        method: 'POST',
        body: props,
      }),
      transformResponse: (response: { message: string }) => response,
    }),
    passwordReset: build.mutation({
      query: (body: { password: string; token: string; email: string }) => ({
        url: '/auth/password/reset',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { message: string }) => response,
    }),
    createUser: build.mutation({
      query: (body: { userAttributes: UserAttributes; sendEmail?: boolean }) => ({
        url: '/user',
        method: 'POST',
        body,
      }),
    }),
    updateUser: build.mutation({
      query: ({ userId, userAttributes }: { userId: number; userAttributes: Partial<UserAttributes> }) => ({
        url: `/user/${userId}`,
        method: 'PATCH',
        body: userAttributes,
      }),
    }),
    deleteUser: build.mutation({
      query: (userId: number) => ({
        url: `/user/${userId}`,
        method: 'DELETE',
      }),
    }),
    assignClientByToken: build.query({
      query: (props: { clientId: number; token: string }) => ({
        url: `/tokenAssignment/assign-client?clientId=${props.clientId}&token=${props.token}`,
        method: 'GET',
      }),
      transformResponse: (response: { message: string }) => response,
    }),
    getAllAssignableUsers: build.query({
      query: (businessUnitId: number | null) => ({
        url:
          businessUnitId != null
            ? `/user/get-all-assignable-users?businessUnitId=${businessUnitId}`
            : '/user/get-all-assignable-users',
        method: 'GET',
      }),
      transformResponse: (
        response: { assignableUsers?: User[]; error?: string } | null
      ): { assignableUsers?: User[]; error?: string } | null => response,
    }),
    getGoogleWorkspaceStatus: build.query({
      query: () => ({ url: '/admin/google-workspace/status', method: 'GET' }),
      transformResponse: (response: { gmailConnected: boolean; meetConnected: boolean }) => response,
    }),
    getGmailConnectUrl: build.query({
      query: () => ({ url: '/admin/google-workspace/gmail/connect', method: 'GET' }),
      transformResponse: (response: { url: string }) => response.url,
    }),
    getMeetConnectUrl: build.query({
      query: () => ({ url: '/admin/google-workspace/meet/connect', method: 'GET' }),
      transformResponse: (response: { url: string }) => response.url,
    }),
    disconnectGmail: build.mutation({
      query: () => ({ url: '/admin/google-workspace/gmail/disconnect', method: 'DELETE' }),
    }),
    disconnectMeet: build.mutation({
      query: () => ({ url: '/admin/google-workspace/meet/disconnect', method: 'DELETE' }),
    }),
    getSellerFilterOptions: build.query<{ brands: string[]; branchOffices: string[] }, { businessUnitId: number }>({
      query: ({ businessUnitId }) => ({
        url: `/user/seller-filter-options/${businessUnitId}`,
        method: 'GET',
      }),
    }),
    uploadQuickReplyFile: build.mutation<QuickReplyFileUpload, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return { url: '/user/quick-replies/file', method: 'POST', body: formData };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useSignInUserMutation,
  useVerifyMfaCodeMutation,
  useLogoutUserMutation,
  useResendMfaCodeMutation,
  useGetUserQuery,
  useGetQuickRepliesQuery,
  useUpdatePasswordMutation,
  useGetAssignableUsersMutation,
  useAssignClientToUserMutation,
  useGetUsersQuery,
  useHandleFirebaseNotificationTokensMutation,
  useUpdateCurrentUserMutation,
  usePasswordRecoveryMutation,
  usePasswordResetMutation,
  useSignInWithGoogleMutation,
  useSignInWithMicrosoftMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useAssignClientByTokenQuery,
  useGetAllAssignableUsersQuery,
  useGetGoogleWorkspaceStatusQuery,
  useLazyGetGmailConnectUrlQuery,
  useLazyGetMeetConnectUrlQuery,
  useDisconnectGmailMutation,
  useDisconnectMeetMutation,
  useGetSellerFilterOptionsQuery,
  useUploadQuickReplyFileMutation,
} = userApi;
